// Design › Wired › Ports, laid out as concept 1A (#1893): the faceplate on its
// own until ports are picked, an inspector for the ports picked, and a sticky
// bar that says what is waiting to be applied.
//
// **Layout only.** Nothing here writes or reads. The inspector holds what the
// page already drew for a selection — the bulk editor (`PortsEditor.tsx`),
// whose whole-set `PUT /switches/{serial}/ports` is unchanged, and the port's
// held-and-planned detail — and the bar's *Review & apply* opens the one act,
// the sheet that reads, plans, confirms and applies (`ReviewApply.tsx`, #1891).
//
// **What the bar counts.** *Pending* is the site's work list's item for this
// switch (`GET /sites/{id}/work-list`, ADR-0117), the read the Changes inbox
// counts from, so the bar and the inbox always agree (#1891): the ports its
// comparison rows name, `{serial}/port/{name}`, without planning first. An
// item with no port row is pending all the same — a VLAN interface or a
// wildcard — and is said so rather than counted as no port. A work list that
// could not be read, or that left this switch past its bound, is said so and
// never counted as zero. *Unsaved* is the ports whose fields, in the
// inspector, differ from their saved values; *Discard* drops those fields and
// closes an open port form, and it reverts nothing that was saved.
import type { ReactNode } from "react";
import { plural } from "../../dashboard/format";
import { Inspector as Frame } from "../../ui";
import { PortName } from "./DeviceFace";

/** How many port names the bar lists before it counts the rest. A visual
 * constant: the bar is one line at a laptop's width. */
const NAMED = 6;

function Names({ ports }: { ports: readonly string[] }) {
  const shown = ports.slice(0, NAMED);
  const rest = ports.length - shown.length;
  return (
    <>
      {shown.map((p, i) => <span key={p}>{i > 0 && ", "}<PortName name={p} /></span>)}
      {rest > 0 && <> and {rest} more</>}
    </>
  );
}

/** The panel a selection opens, beside the ports, in the shared `Inspector`
 * frame (#1890). Drawn only while a port is picked; `onClose` empties the
 * selection, which is what closes it, as Escape inside it does. */
export function Inspector({ picked, onClose, children }: { picked: readonly string[]; onClose(): void; children: ReactNode }) {
  const single = picked.length === 1 ? picked[0] : undefined;
  return (
    <Frame title={single ? <code>{single}</code> : <>{picked.length} {plural(picked.length, "port")}</>} onClose={onClose}>
      {children}
    </Frame>
  );
}

/** What the bar says is pending, from the work list (#1891). */
export type Pending =
  | { state: "loading" }
  | { state: "unknown" }
  | { state: "ready"; ports: readonly string[]; other: boolean };

/** The sticky bar under the ports: *N pending · ports …*, the inspector's
 * unsaved fields, *Discard* and *Review & apply*. `onReview` is absent where
 * there is no apply — a planned switch (#521) — and so is the pending count. */
export function PendingBar({ pending = { state: "loading" }, unsaved, onDiscard, onReview, reviewing = false }: {
  pending?: Pending;
  unsaved: readonly string[];
  onDiscard(): void;
  onReview?(): void;
  reviewing?: boolean;
}) {
  return (
    <div className="pending-bar" role="region" aria-label="Pending changes">
      {onReview && (
        <span className="pending-bar-what">
          {pending.state === "loading"
            ? "Reading what is pending…"
            : pending.state === "unknown"
              ? "Pending not known"
              : pending.ports.length > 0
                ? <>{pending.ports.length} pending · {plural(pending.ports.length, "port")} <Names ports={pending.ports} />{pending.other && " and more"}</>
                : pending.other ? "Pending outside the ports" : "Nothing pending"}
        </span>
      )}
      {unsaved.length > 0 && <span className="pending-bar-unsaved">{unsaved.length} unsaved · <Names ports={unsaved} /></span>}
      <span className="pending-bar-acts">
        <button type="button" className="btn" aria-label="Discard unsaved changes" onClick={onDiscard} disabled={unsaved.length === 0}>Discard</button>
        {onReview && <button type="button" className="btn btn-primary" onClick={onReview} disabled={reviewing}>Review &amp; apply</button>}
      </span>
    </div>
  );
}
